/**
 * OrderManager — Position management in right-side cart panel.
 *
 * Each "position" is a glass configuration (type, class, insulation, etc.).
 * Within each position, the user can add multiple "variants" — each with
 * its own quantity, width, and height. Computed columns show M²/STK, M²/POS, KG/STK.
 *
 * Features:
 * - Add current configurator state as a new position
 * - Multiple size/qty variants per position
 * - Computed columns: M²/STK, M²/POS, KG/STK
 * - Position summary footer with totals
 * - Inline-editable variant fields (auto-save + live recompute)
 * - Edit position config (loads into configurator)
 * - Duplicate / delete positions
 * - Cart panel footer with grand totals
 * - localStorage persistence
 */

// German display labels for internal filter values
const DE_LABELS = {
  '1-glazed (mono)': 'Mono',
  '2-glazed-ISO': '2-fach ISO',
  '3-glazed-ISO': '3-fach ISO',
  'Inside': 'Innen',
  'Inside/Outside': 'Innen / Aussen',
  'Outside/Outside': 'Aussen / Aussen',
};

function deLabel(val) {
  return DE_LABELS[val] || val;
}

export default class OrderManager {
  constructor(bridge) {
    this.bridge = bridge;
    this.orders = [];
    this.editingOrderId = null;

    // Templates
    this.positionTemplate = document.getElementById('position-template');
    this.variantTemplate = document.getElementById('variant-row-template');

    // DOM refs — right-side cart panel
    this.gridEl = document.getElementById('orders-grid');
    this.countEl = document.getElementById('order-count');
    this.emptyEl = document.getElementById('empty-orders');
    this.actionsEl = document.getElementById('order-actions');
    this.cartBadge = document.getElementById('cart-badge');

    // Cart footer totals
    this.cartTotalSqm = document.getElementById('cart-total-sqm');
    this.cartTotalKg = document.getElementById('cart-total-kg');

    // Button elements (in canvas action bar)
    this.addBtn = document.getElementById('add-to-order');
    this.defaultActionsEl = document.getElementById('canvas-actions-default');
    this.editActionsEl = document.getElementById('canvas-actions-edit');
    this.updateBtn = document.getElementById('update-order');
    this.addAsNewBtn = document.getElementById('add-as-new');
    this.cancelEditBtn = document.getElementById('cancel-edit');

    this._load();
    this._initAddButton();
    this._initEditModeButtons();
    this._initCartToggle();
    this._initResetButton();
    this._render();
  }

  // ── Init: Add position button ─────────────────────────────

  _initAddButton() {
    if (this.addBtn) {
      this.addBtn.addEventListener('click', () => {
        this.addCurrentConfig();
        this.addBtn.style.transform = 'scale(0.95)';
        setTimeout(() => { this.addBtn.style.transform = ''; }, 150);
      });
    }
  }

  // ── Init: Edit mode buttons ───────────────────────────────

  _initEditModeButtons() {
    if (this.updateBtn) {
      this.updateBtn.addEventListener('click', () => {
        if (!this.editingOrderId) return;
        const config = this._snapshotConfig();
        const idx = this.orders.findIndex(o => o.id === this.editingOrderId);
        if (idx !== -1) {
          config.id = this.editingOrderId;
          config.variants = this.orders[idx].variants;
          this.orders[idx] = config;
          this._save();
          this._render();
        }
        this._switchToAddMode();
      });
    }

    if (this.addAsNewBtn) {
      this.addAsNewBtn.addEventListener('click', () => {
        this.addCurrentConfig();
        this._switchToAddMode();
      });
    }

    if (this.cancelEditBtn) {
      this.cancelEditBtn.addEventListener('click', () => {
        this._switchToAddMode();
      });
    }
  }

  // ── Init: Cart toggle (header truck icon) ─────────────────

  _initCartToggle() {
    const cartToggle = document.getElementById('cart-toggle');
    if (cartToggle) {
      cartToggle.addEventListener('click', () => {
        if (window.innerWidth <= 768) {
          // Mobile: toggle bottom sheet
          if (window._isSheetExpanded && window._isSheetExpanded()) {
            if (window._collapseBottomSheet) window._collapseBottomSheet();
          } else {
            if (window._switchToOrdersTab) window._switchToOrdersTab();
          }
        } else {
          // Desktop: scroll cart to top to show latest items
          const cartItems = document.getElementById('cart-items');
          if (cartItems) cartItems.scrollTo({ top: cartItems.scrollHeight, behavior: 'smooth' });
        }
      });
    }
  }

  // ── Init: Reset all positions ─────────────────────────────

  _initResetButton() {
    const resetBtn = document.getElementById('reset-orders');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (this.orders.length === 0) return;
        const ok = confirm('Alle Positionen löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.');
        if (!ok) return;
        this.orders = [];
        this._switchToAddMode();
        this._save();
        this._render();
      });
    }
  }

  // ── Snapshot current configurator state ───────────────────

  _snapshotConfig() {
    const w = document.getElementById('dim-width')?.value || '';
    const h = document.getElementById('dim-height')?.value || '';

    // Capture weight per m² from current glass
    let weightPerSqm = 0;
    const structure = window._cdrStructure;
    if (structure && structure.fireResistentGlass) {
      const fw = structure.fireResistentGlass.weight;
      if (!isNaN(fw)) weightPerSqm = parseFloat(fw);
    }

    const config = {
      id: Date.now(),
      structure: deLabel(this.bridge.getFilterValue('structure')) || '—',
      manufacturer: this.bridge.getFilterValue('manufacturer') || '—',
      class: this.bridge.getFilterValue('class') || '—',
      insulation: this.bridge.getFilterValue('insulation') || '—',
      application: deLabel(this.bridge.getFilterValue('application')) || '—',
      whiteGlass: this.bridge.getFilterValue('white-glass') ? 'Ja' : 'Nein',
      resistance: this.bridge.getFilterValue('resistance') || '—',
      buttJoints: this.bridge.getFilterValue('butt-joints') ? 'Ja' : 'Nein',
      weightPerSqm: weightPerSqm,
      timestamp: new Date().toLocaleString('de-CH'),
      variants: [
        { id: Date.now(), quantity: 1, width: w, height: h }
      ],
    };

    // Glass names
    const frgSelect = document.querySelector('.fire-resistant');
    const mgSelect = document.querySelector('.middle-glass');
    const ogSelect = document.querySelector('.outside-glass');
    config.frgName = frgSelect?.options[frgSelect.selectedIndex]?.text || '—';
    config.mgName = mgSelect?.options[mgSelect.selectedIndex]?.text || '—';
    config.ogName = ogSelect?.options[ogSelect.selectedIndex]?.text || '—';

    return config;
  }

  // ── Compute helpers ─────────────────────────────────────────

  _computeSqmStk(variant) {
    const w = parseFloat(variant.width);
    const h = parseFloat(variant.height);
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return 0;
    return (w * h) / 1000000;
  }

  _computeSqmPos(variant) {
    return this._computeSqmStk(variant) * (parseInt(variant.quantity) || 1);
  }

  _computeKgStk(variant, weightPerSqm) {
    if (!weightPerSqm) return 0;
    return this._computeSqmStk(variant) * weightPerSqm;
  }

  _formatNum(val, decimals = 2) {
    if (!val || val === 0) return '—';
    return val.toFixed(decimals);
  }

  // ── CRUD ──────────────────────────────────────────────────

  addCurrentConfig() {
    const config = this._snapshotConfig();
    this.orders.push(config);
    this._save();
    this._render();

    // On mobile: swipe to positions tab and highlight the new entry
    if (window.innerWidth <= 768) {
      if (window._switchToOrdersTab) window._switchToOrdersTab();
      if (window._expandBottomSheet) window._expandBottomSheet();

      setTimeout(() => {
        const blocks = this.gridEl?.querySelectorAll('.position-block');
        const lastBlock = blocks?.[blocks.length - 1];
        if (lastBlock) {
          lastBlock.classList.add('pos-highlight');
          lastBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          setTimeout(() => lastBlock.classList.remove('pos-highlight'), 2500);
        }
      }, 400);
    }
  }

  removeOrder(id) {
    this.orders = this.orders.filter(o => o.id !== id);
    if (this.editingOrderId === id) this._switchToAddMode();
    this._save();
    this._render();
  }

  loadConfig(order) {
    const filterMap = {
      structure: order.structure,
      manufacturer: order.manufacturer,
      class: order.class,
      insulation: order.insulation,
      application: order.application,
      resistance: order.resistance,
    };

    Object.entries(filterMap).forEach(([filterName, value]) => {
      if (value && value !== '—') {
        this.bridge.setFilterValue(filterName, value);
      }
    });

    if (order.whiteGlass === 'Ja') this.bridge.setFilterValue('white-glass', true);
    if (order.buttJoints === 'Ja') this.bridge.setFilterValue('butt-joints', true);
  }

  editOrder(order) {
    this.loadConfig(order);
    this.editingOrderId = order.id;
    this._switchToEditMode();
    this._render();

    // On mobile, slide over to the settings tab
    if (window._switchToSettingsTab) window._switchToSettingsTab();
  }

  duplicateOrder(order) {
    const clone = JSON.parse(JSON.stringify(order));
    clone.id = Date.now();
    clone.timestamp = new Date().toLocaleString('de-CH');
    clone.variants = clone.variants.map((v, i) => ({
      ...v,
      id: Date.now() + i + 1,
    }));
    this.orders.push(clone);
    this._save();
    this._render();
  }

  // ── Variant CRUD ──────────────────────────────────────────

  addVariant(orderId) {
    const order = this.orders.find(o => o.id === orderId);
    if (!order) return;
    order.variants.push({
      id: Date.now(),
      quantity: 1,
      width: '',
      height: '',
    });
    this._save();
    this._render();
  }

  removeVariant(orderId, variantId) {
    const order = this.orders.find(o => o.id === orderId);
    if (!order || order.variants.length <= 1) return;
    order.variants = order.variants.filter(v => v.id !== variantId);
    this._save();
    this._render();
  }

  updateVariant(orderId, variantId, field, value) {
    const order = this.orders.find(o => o.id === orderId);
    if (!order) return;
    const variant = order.variants.find(v => v.id === variantId);
    if (!variant) return;

    if (field === 'quantity') {
      variant.quantity = Math.max(1, parseInt(value) || 1);
    } else {
      variant[field] = value;
    }
    this._save();
  }

  // ── Edit mode switching ───────────────────────────────────

  _switchToEditMode() {
    if (this.defaultActionsEl) this.defaultActionsEl.classList.add('hidden');
    if (this.editActionsEl) this.editActionsEl.classList.remove('hidden');
  }

  _switchToAddMode() {
    this.editingOrderId = null;
    if (this.defaultActionsEl) this.defaultActionsEl.classList.remove('hidden');
    if (this.editActionsEl) this.editActionsEl.classList.add('hidden');
    this._render();
  }

  // ── Render ────────────────────────────────────────────────

  _render() {
    if (!this.gridEl || !this.positionTemplate) return;

    this.gridEl.innerHTML = '';

    const hasOrders = this.orders.length > 0;

    // Toggle empty state / footer
    if (this.emptyEl) this.emptyEl.classList.toggle('hidden', hasOrders);
    if (this.actionsEl) this.actionsEl.classList.toggle('hidden', !hasOrders);
    if (this.countEl) this.countEl.textContent = this.orders.length;

    // Cart badge (truck icon in action bar)
    if (this.cartBadge) {
      this.cartBadge.textContent = this.orders.length;
      this.cartBadge.classList.toggle('hidden', !hasOrders);
    }

    // Grand totals for cart footer
    let grandTotalSqm = 0;
    let grandTotalKg = 0;

    this.orders.forEach((order, posIdx) => {
      const clone = this.positionTemplate.content.cloneNode(true);
      const block = clone.querySelector('.position-block');

      // Editing highlight
      if (this.editingOrderId === order.id) {
        block.classList.add('editing');
      }

      // --- Header ---
      const numEl = block.querySelector('.position-number');
      const titleEl = block.querySelector('.position-title');
      if (numEl) numEl.textContent = `Pos. ${posIdx + 1}`;
      if (titleEl) titleEl.textContent = order.frgName || order.class || '—';

      // --- Config pills ---
      const configEl = block.querySelector('.position-config');
      if (configEl) {
        const configItems = [
          ['Aufbau', order.structure],
          ['Klasse', order.class],
          ['Isolation', order.insulation],
          ['Anwendung', order.application],
          ['Hersteller', order.manufacturer !== 'Alle' ? order.manufacturer : null],
          ['Weissglas', order.whiteGlass === 'Ja' ? 'Ja' : null],
          ['Widerstand', order.resistance !== '—' ? order.resistance : null],
          ['Stossfugen', order.buttJoints === 'Ja' ? 'Ja' : null],
        ];

        configItems.forEach(([label, value]) => {
          if (!value) return;
          const pill = document.createElement('span');
          pill.className = 'config-pill';
          pill.innerHTML = `<span class="config-pill-label">${label}</span><span class="config-pill-value">${value}</span>`;
          configEl.appendChild(pill);
        });

        // Collapse config details by default for compact view
        configEl.classList.add('collapsed');
      }

      // --- Position actions ---
      block.querySelector('.position-edit')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const btn = e.currentTarget;
        const icon = btn.querySelector('i');
        // Swap to spinner
        if (icon) {
          icon.className = 'fas fa-circle-notch fa-spin';
        }
        // Short delay so the user sees the spinner, then load + swipe
        setTimeout(() => {
          this.editOrder(order);
          // Restore icon after swipe completes
          setTimeout(() => {
            if (icon) icon.className = 'fas fa-pencil-alt';
          }, 400);
        }, 300);
      });
      block.querySelector('.position-duplicate')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.duplicateOrder(order);
      });
      block.querySelector('.position-delete')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeOrder(order.id);
      });

      // Config toggle
      const toggleBtn = block.querySelector('.position-toggle');
      if (toggleBtn && configEl) {
        toggleBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          configEl.classList.toggle('collapsed');
          const icon = toggleBtn.querySelector('i');
          if (icon) {
            icon.classList.toggle('fa-chevron-down');
            icon.classList.toggle('fa-chevron-up');
          }
        });
      }

      // --- Variant rows ---
      const tableBody = block.querySelector('.position-table-body');
      if (tableBody) {
        tableBody.setAttribute('data-count', order.variants.length);

        order.variants.forEach((variant, varIdx) => {
          const vClone = this.variantTemplate.content.cloneNode(true);
          const row = vClone.querySelector('.variant-row');

          // Row number (1.1, 1.2, ...)
          const rowNum = row.querySelector('.row-number');
          if (rowNum) rowNum.textContent = `${posIdx + 1}.${varIdx + 1}`;

          // Populate inputs
          const inputs = row.querySelectorAll('.variant-input');
          inputs.forEach(input => {
            const field = input.dataset.field;
            if (field === 'quantity') input.value = variant.quantity || 1;
            else if (field === 'width') input.value = variant.width || '';
            else if (field === 'height') input.value = variant.height || '';

            // Auto-save + recompute on change
            const onChange = () => {
              this.updateVariant(order.id, variant.id, field, input.value);
              if (field === 'quantity') input.value = Math.max(1, parseInt(input.value) || 1);
              this._recomputeRow(row, variant, order.weightPerSqm);
              this._recomputeFooter(block, order);
              this._updateCartTotals();
            };
            input.addEventListener('change', onChange);
            input.addEventListener('input', () => {
              // Live preview of computed values while typing
              const tempVariant = { ...variant };
              if (field === 'width') tempVariant.width = input.value;
              else if (field === 'height') tempVariant.height = input.value;
              else if (field === 'quantity') tempVariant.quantity = input.value;
              this._recomputeRow(row, tempVariant, order.weightPerSqm);
              this._recomputeFooter(block, order, posIdx);
            });
          });

          // Remove variant button
          const removeBtn = row.querySelector('.variant-remove');
          if (removeBtn) {
            removeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              this.removeVariant(order.id, variant.id);
            });
          }

          // Initial computed values
          this._recomputeRow(row, variant, order.weightPerSqm);

          tableBody.appendChild(row);
        });

        // Add variant link after table body
        const addLink = document.createElement('button');
        addLink.className = 'add-variant-link';
        addLink.innerHTML = '<i class="fas fa-plus"></i> Variante';
        addLink.addEventListener('click', (e) => {
          e.stopPropagation();
          this.addVariant(order.id);
        });
        tableBody.after(addLink);
      }

      // --- Summary footer ---
      this._renderFooter(block, order, posIdx);

      // Accumulate grand totals
      order.variants.forEach(v => {
        const qty = parseInt(v.quantity) || 0;
        const sqmStk = this._computeSqmStk(v);
        const kgStk = this._computeKgStk(v, order.weightPerSqm);
        grandTotalSqm += sqmStk * qty;
        grandTotalKg += kgStk * qty;
      });

      // Stagger animation
      block.style.animationDelay = `${posIdx * 0.05}s`;

      this.gridEl.appendChild(block);
    });

    // Update cart footer totals
    if (this.cartTotalSqm) this.cartTotalSqm.textContent = this._formatNum(grandTotalSqm);
    if (this.cartTotalKg) this.cartTotalKg.textContent = this._formatNum(grandTotalKg, 1);
  }

  // ── Recompute a single row's computed cells ─────────────────

  _recomputeRow(row, variant, weightPerSqm) {
    const sqmStk = this._computeSqmStk(variant);
    const sqmPos = this._computeSqmPos(variant);
    const kgStk = this._computeKgStk(variant, weightPerSqm);

    const cells = row.querySelectorAll('.computed-value');
    if (cells[0]) cells[0].textContent = this._formatNum(sqmStk);
    if (cells[1]) cells[1].textContent = this._formatNum(sqmPos);
    if (cells[2]) cells[2].textContent = this._formatNum(kgStk, 1);
  }

  // ── Render summary footer ──────────────────────────────────

  _renderFooter(block, order) {
    const foot = block.querySelector('.position-table-foot');
    if (!foot) return;
    foot.innerHTML = '';

    let totalQty = 0;
    let totalSqm = 0;
    let totalKg = 0;

    order.variants.forEach(v => {
      const qty = parseInt(v.quantity) || 0;
      const sqmStk = this._computeSqmStk(v);
      const kgStk = this._computeKgStk(v, order.weightPerSqm);
      totalQty += qty;
      totalSqm += sqmStk * qty;
      totalKg += kgStk * qty;
    });

    // Build footer grid cells matching table columns
    const cells = [
      { content: `<span class="summary-label">Gesamt</span>`, class: 'cell-nr' },
      { content: '', class: 'cell-width' },
      { content: '', class: 'cell-height' },
      { content: `<span class="summary-value">${totalQty}</span>`, class: 'cell-qty' },
      { content: `<span class="summary-value">${this._formatNum(totalSqm)}</span>`, class: 'cell-sqm' },
      { content: '', class: 'cell-sqm-pos' },
      { content: `<span class="summary-value">${this._formatNum(totalKg, 1)}</span>`, class: 'cell-kg' },
      { content: '', class: 'cell-actions' },
    ];

    cells.forEach(c => {
      const cell = document.createElement('div');
      cell.className = `variant-cell ${c.class}`;
      cell.innerHTML = c.content;
      foot.appendChild(cell);
    });
  }

  _recomputeFooter(block, order) {
    this._renderFooter(block, order);
  }

  // ── Update cart panel grand totals ──────────────────────────

  _updateCartTotals() {
    let grandSqm = 0;
    let grandKg = 0;
    this.orders.forEach(order => {
      order.variants.forEach(v => {
        const qty = parseInt(v.quantity) || 0;
        grandSqm += this._computeSqmStk(v) * qty;
        grandKg += this._computeKgStk(v, order.weightPerSqm) * qty;
      });
    });
    if (this.cartTotalSqm) this.cartTotalSqm.textContent = this._formatNum(grandSqm);
    if (this.cartTotalKg) this.cartTotalKg.textContent = this._formatNum(grandKg, 1);
  }

  // ── Persistence ───────────────────────────────────────────

  _save() {
    try {
      localStorage.setItem('cdr-configurator-orders', JSON.stringify(this.orders));
    } catch (e) {
      console.warn('Failed to save orders to localStorage:', e);
    }
  }

  _load() {
    try {
      const saved = localStorage.getItem('cdr-configurator-orders');
      if (saved) {
        this.orders = JSON.parse(saved);
        this._migrate();
      }
    } catch (e) {
      console.warn('Failed to load orders from localStorage:', e);
      this.orders = [];
    }
  }

  /**
   * Migrate old formats to current structure.
   */
  _migrate() {
    let changed = false;
    this.orders.forEach(order => {
      // Migrate flat format to nested variants
      if (!order.variants) {
        order.variants = [{
          id: Date.now() + Math.random(),
          quantity: order.quantity || 1,
          width: order.width || '',
          height: order.height || '',
        }];
        delete order.quantity;
        delete order.width;
        delete order.height;
        changed = true;
      }
      // Add weightPerSqm if missing
      if (order.weightPerSqm === undefined) {
        order.weightPerSqm = 0;
        changed = true;
      }
    });
    if (changed) this._save();
  }
}
